import React, { memo, useCallback, useMemo } from 'react'
import { isSection } from './fieldTypes'
import { buildTree } from './StructurePanel'
import { useT } from './translations'
import { conditionSummary } from './ConditionEditor'
import { hasCondition } from '../conditions'

const Control = ({ field }) => {
  const t = useT()

  switch (field.data_type) {
    case 'description':
      return <div className="fb-fake-input is-area" />
    case 'number':
    case 'float':
      return <div className="fb-fake-input is-short">0</div>
    case 'date':
      return <div className="fb-fake-input is-short"><i className="far fa-calendar-alt" /> 01/01/2026</div>
    case 'date_time':
      return <div className="fb-fake-input is-short"><i className="far fa-clock" /> 01/01/2026 08:00</div>
    case 'yes_no':
      return <div className="fb-fake-choices"><span className="fb-fake-radio" /> {t.yes_label} <span className="fb-fake-radio" /> {t.no_label}</div>
    case 'checkbox':
      return null
    case 'select':
    case 'select_prefill': {
      const options = field.options || []
      if (options.length === 0) { return <div className="fb-fake-input is-muted">{t.no_options}</div> }
      return (
        <div className="fb-fake-choices is-list">
          {options.slice(0, 6).map((option) => (
            <span key={option}><span className={field.multi_option ? 'fb-fake-check' : 'fb-fake-radio'} /> {option}</span>
          ))}
          {options.length > 6 && <span className="is-muted">+{options.length - 6}</span>}
          {field.has_extra_option && <span><span className="fb-fake-radio" /> {t.other}</span>}
        </div>
      )
    }
    case 'table': {
      const config  = field.table_columns || { columns: [] }
      const columns = config.columns || []
      const rows    = Math.max(config.min_rows || 1, (config.default_rows || []).length, 2)
      return (
        <table className="fb-fake-table">
          <thead>
            <tr>{columns.map((column) => <th key={column.key} style={{ width: `${column.width}%` }}>{column.name}</th>)}</tr>
          </thead>
          <tbody>
            {Array.from({ length: Math.min(rows, 4) }, (_, index) => (
              <tr key={index}>
                {columns.map((column) => <td key={column.key}>{(config.default_rows || [])[index]?.[column.key] ?? ''}</td>)}
              </tr>
            ))}
            {config.show_totals && (
              <tr className="is-total">
                {columns.map((column, index) => <td key={column.key}>{index === 0 ? t.total : (column.total ? 'Σ' : '')}</td>)}
              </tr>
            )}
          </tbody>
        </table>
      )
    }
    case 'e_sign':
      return <div className="fb-fake-input is-sign"><i className="fas fa-signature" /></div>
    case 'photos':
      return <div className="fb-fake-input is-photo"><i className="fas fa-camera" /> {t.add_photos}</div>
    default:
      return <div className="fb-fake-input" />
  }
}

const ConditionBadge = ({ text }) => {
  if (!text) { return null }
  return <div className="fb-condition-badge"><i className="fas fa-code-branch" /> {text}</div>
}

const conditionText = (field, fields, t) => (
  hasCondition(field.visible_when) ? (conditionSummary(field.visible_when, fields, t) || t.condition_incomplete) : null
)

const PreviewField = memo(({ field, condition, selected, onSelect }) => {
  const t = useT()

  if (field.data_type === 'page_break') {
    return (
      <div className={`fb-preview-field is-break${selected ? ' selected' : ''}`} onClick={() => onSelect(field.id)}>
        <span>{t.types.page_break}</span>
      </div>
    )
  }

  if (field.data_type === 'static') {
    return (
      <div className={`fb-preview-field${selected ? ' selected' : ''}`} style={{ textAlign: field.text_align }} onClick={() => onSelect(field.id)}>
        <div className="fb-preview-static">{field.name || t.untitled}</div>
      </div>
    )
  }

  return (
    <div className={`fb-preview-field${selected ? ' selected' : ''}${hasCondition(field.visible_when) ? ' is-conditional' : ''}`} onClick={() => onSelect(field.id)}>
      <ConditionBadge text={condition} />
      <label className="fb-preview-label">
        {field.data_type === 'checkbox' && <span className="fb-fake-check" />}
        {field.name || <span className="is-muted">{t.untitled}</span>}
        {field.is_mandatory && <span className="fb-required">*</span>}
      </label>
      <Control field={field} />
    </div>
  )
})

const FormPreview = ({ blueprint, fields, selection, actions }) => {
  const t = useT()
  const tree = useMemo(() => buildTree(fields), [fields])
  const select = useCallback((id) => actions.setSelection([id]), [actions])

  return (
    <div className="fb-preview-scroll" onClick={(event) => { if (event.target === event.currentTarget) { actions.setSelection([]) } }}>
      <div className="fb-paper" style={{ textAlign: blueprint.text_align }}>
        <h2 className="fb-paper-title">{blueprint.display_name || blueprint.name}</h2>

        {tree.roots.length === 0 && <div className="fb-empty">{t.empty_form}</div>}

        {tree.roots.map((field) => {
          if (!isSection(field)) {
            return <PreviewField key={field.id} field={field} condition={conditionText(field, fields, t)} selected={selection.includes(field.id)} onSelect={select} />
          }

          const children = tree.children[field.id] || []
          return (
            <section key={field.id} className={`fb-preview-section${selection.includes(field.id) ? ' selected' : ''}`}>
              <ConditionBadge text={conditionText(field, fields, t)} />
              <h3 style={{ textAlign: field.text_align }} onClick={() => select(field.id)}>
                {field.name || t.untitled}
                {field.duplicable && <span className="fb-badge"><i className="fas fa-redo" /> {t.duplicable}</span>}
              </h3>
              {children.map((child) => (
                <PreviewField key={child.id} field={child} condition={conditionText(child, fields, t)} selected={selection.includes(child.id)} onSelect={select} />
              ))}
              {children.length === 0 && <div className="fb-empty">{t.empty_section}</div>}
            </section>
          )
        })}
      </div>
    </div>
  )
}

export default FormPreview
